/*!
 * PXG Australia club configurator - pricing & options engine.
 * Pure functions, no DOM. Shared by the theme (window.PXGEngine) and the Node test suite.
 * Data source: pxg_* metaobjects (Shopify admin > Content > Metaobjects).
 *
 * Price per club = head price (steel/base or graphite) + shaft upcharge + grip upcharge.
 * Build total     = price per club x number of clubs.
 * Cart (Basic plan): head variant per club + one "PXG Custom Upgrade" variant per upcharge amount x clubs.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.PXGEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var FLEX_BUCKETS = ['Ladies', 'Senior', 'Regular', 'Stiff', 'X-Stiff'];
  var DEFAULT_GRIP = 'pxg-superstroke-s-tech'; // "PXG Logo Grip"

  // ---------- helpers ----------
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function cents(v) { return Math.round(num(v) * 100); }
  function bool(v) { return v === true || v === 'true'; }
  function arr(v) { return Array.isArray(v) ? v : (v ? [v] : []); }
  function money(c) {
    var s = (Math.abs(c) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (c < 0 ? '-$' : '$') + s;
  }
  function uniq(a) { var o = []; a.forEach(function (x) { if (o.indexOf(x) < 0) o.push(x); }); return o; }

  /** Which pxg_build_setting a head uses. */
  function settingKey(head) {
    if (head.build_setting) return head.build_setting;
    if (head.handle === 'gen8-driving-iron' || arr(head.lofts).join() === 'DI') return 'driving-iron';
    return String(head.club_type || '').toLowerCase();
  }
  /** Category drives the UI flow (steps). */
  function category(head) {
    var k = settingKey(head);
    if (k.indexOf('putter') === 0) return 'putter';
    if (k === 'driving-iron') return 'driving-iron';
    if (head.club_type === 'Wood') return /driver/i.test(head.model || head.label) ? 'driver' : 'fairway';
    return String(head.club_type || '').toLowerCase(); // iron | wedge | hybrid
  }
  function gripClubType(head) {
    var c = category(head);
    if (c === 'putter') return 'Putter';
    if (c === 'driver' || c === 'fairway') return 'Wood';
    if (c === 'hybrid') return 'Hybrid';
    return 'Iron'; // iron, wedge, driving-iron
  }
  function shaftCategories(head, rules) {
    var c = category(head), cats;
    if (c === 'putter') cats = ['Putter'];
    else if (c === 'driver' || c === 'fairway') cats = ['Wood'];
    else if (c === 'hybrid') cats = ['Hybrid'];
    else cats = ['Iron'];
    (rules || []).forEach(function (r) {
      if (!bool(r.active) || r.component_type !== 'shaft' || r.effect !== 'allow') return;
      if (r.scope_type === 'model' && r.scope === head.handle) {
        var m = /^\*Category=(\w+)/.exec(r.component_id || '');
        if (m && cats.indexOf(m[1]) < 0) cats.push(m[1]);
      }
    });
    return cats;
  }
  /**
   * Deny rules. component_id is a handle, or a pattern "*Category=Iron;Material=Graphite".
   * scope_type: builder (everywhere) | club_type (head.club_type, e.g. Iron excludes Wedge) | model (head handle).
   */
  function matchComp(id, rec) {
    if (id === rec.handle) return true;
    if (id.charAt(0) !== '*') return false;
    return id.slice(1).split(';').every(function (kv) { var p = kv.split('='); return String(rec[p[0].toLowerCase()]) === p[1]; });
  }
  function denied(rules, head, type, rec) {
    return (rules || []).some(function (r) {
      if (!bool(r.active) || r.effect !== 'deny' || r.component_type !== type) return false;
      if (!matchComp(r.component_id || '', rec)) return false;
      if (r.scope_type === 'builder') return true;
      if (r.scope_type === 'club_type') return r.scope === head.club_type;
      if (r.scope_type === 'model') return r.scope === head.handle;
      return false;
    });
  }
  /** Lofts available left-handed, parsed from the lh note ("9° only - Custom order", "9°, 10.5° - ..."). */
  function lhLofts(h) {
    var m = /^([\d.°,\s]+?)(?:\s*only)?\s*[—–-]/.exec(h.lh || '');
    if (!m) return null;
    var l = m[1].split(',').map(function (x) { return x.replace(/[°\s]/g, ''); }).filter(Boolean);
    return l.length ? l : null;
  }
  function lhAvailable(h) { return !/not available/i.test(h.lh || ''); }

  // ---------- catalogue ----------
  function Catalogue(data) {
    this.heads = (data.heads || []).filter(function (h) {
      if (h.status !== 'Current' || !(num(h.base_price_aud) > 0)) return false;
      return h.club_type !== 'Putter' || !!h.build_setting; // putters need a build setting to be web-buildable
    });
    this.shafts = (data.shafts || []).filter(function (s) { return bool(s.active) && bool(s.builder); })
      .sort(function (a, b) { return num(a.display_order) - num(b.display_order); });
    this.grips = (data.grips || []).filter(function (g) { return bool(g.active) && bool(g.builder); })
      .sort(function (a, b) { return num(a.display_order) - num(b.display_order); });
    this.rules = data.rules || [];
    this.settings = {};
    var self = this;
    (data.settings || []).forEach(function (s) { self.settings[s.handle] = s; });
    this.variants = data.variants || { heads: {}, upgrades: {} };
  }
  Catalogue.prototype.head = function (handle) {
    for (var i = 0; i < this.heads.length; i++) if (this.heads[i].handle === handle) return this.heads[i];
    return null;
  };
  Catalogue.prototype.setting = function (head) { return this.settings[settingKey(head)] || {}; };

  /** Shafts allowed on a head (all materials). Each option = shaft + one flex code. */
  Catalogue.prototype.shaftOptions = function (head) {
    var cats = shaftCategories(head, this.rules), rules = this.rules, longPutter = settingKey(head) === 'putter-long';
    var out = [];
    this.shafts.forEach(function (s) {
      if (cats.indexOf(s.category) < 0) return;
      if (denied(rules, head, 'shaft', s)) return;
      if (s.category === 'Putter' && (/long/.test(s.handle) !== longPutter)) return;
      var flex = arr(s.flex_options);
      if (!flex.length) out.push({ key: s.handle, shaft: s, material: s.material, flex: null, bucket: null, label: s.label, up: cents(s.upcharge_aud) });
      flex.forEach(function (f) {
        out.push({
          key: s.handle + '|' + f.code, shaft: s, material: s.material, flex: f.code, bucket: f.bucket, weight: f.weight || null,
          label: s.label + ' ' + f.code + (f.weight && s.label.indexOf(f.weight) < 0 ? ' (' + f.weight + ')' : ''),
          up: cents(s.upcharge_aud)
        });
      });
    });
    return out;
  };
  Catalogue.prototype.gripOptions = function (head) {
    var ct = gripClubType(head), rules = this.rules, fam = head.family, longPutter = settingKey(head) === 'putter-long';
    return this.grips.filter(function (g) {
      if (arr(g.club_types).indexOf(ct) < 0) return false;
      if (denied(rules, head, 'grip', g)) return false;
      if (ct === 'Putter') {
        if (g.handle === 'putter-pxg-standard-putter-grip-supplied') return false;
        if (/split/.test(g.handle)) return longPutter;
        if (longPutter) return false;
        if (/slant/.test(g.handle)) return fam === 'Zero Torque' || /zero torque/i.test(head.model || '');
      }
      return true;
    });
  };

  // ---------- build state ----------
  /** Default state for a set of heads (one product page). */
  function defaults(cat, heads) {
    var h = heads[0], s = cat.setting(h), c = category(h);
    var st = { head: h.handle, hand: 'Right', finish: firstFinish(h), hosel: firstHosel(h, firstFinish(h)) };
    st.clubs = defaultClubs(h);
    st.loft = c === 'wedge' ? defaultLoftOption(h) : null;
    if (c === 'wedge') st.clubs = [st.loft];
    var mats = materials(cat, h);
    st.material = mats.indexOf('Steel') >= 0 ? 'Steel' : mats[0] || null;
    st.bucket = defaultBucket(cat, h, st.material);
    st.shaft = defaultShaft(cat, h, st.material, st.bucket);
    st.grip = h.default_grip || (c === 'putter' ? null : DEFAULT_GRIP);
    if (!st.grip) { var gs = cat.gripOptions(h); st.grip = gs.length ? gs[0].handle : null; }
    st.gripSize = defaultGripSize(cat, st.grip);
    st.loftAdj = 'Standard'; st.lieAdj = 'Standard';
    st.length = s.default_length || 'Standard';
    st.papers = arr(s.grip_papers)[0] || null;
    st.install = arr(s.grip_install)[0] || null;
    return st;
  }
  function firstFinish(h) { var f = arr(h.finish_options); return f[0] || h.finish || null; }
  function hosels(h, finish) {
    var ho = h.hosel_options;
    if (!ho) return [];
    if (Array.isArray(ho)) return ho;
    return arr(ho[finish] || ho[Object.keys(ho)[0]]);
  }
  function firstHosel(h, finish) {
    var l = hosels(h, finish);
    return l.indexOf("Plumber's Neck") >= 0 ? "Plumber's Neck" : (l[0] || null);
  }
  function loftLabel(h, l) {
    var c = category(h);
    if (c === 'iron') return l;
    if (c === 'fairway') return l + ' Wood';
    if (c === 'driving-iron') return 'Driving Iron';
    return l + '°';
  }
  function defaultClubs(h) {
    var lofts = arr(h.lofts), c = category(h);
    if (c === 'iron') {
      var std = ['5', '6', '7', '8', '9', 'PW', 'GW'].filter(function (x) { return lofts.indexOf(x) >= 0; });
      return std.length ? std : lofts.slice(0, 1);
    }
    if (c === 'putter') return ['Putter'];
    if (c === 'wedge') return [];
    if (c === 'driver') return [lofts.indexOf('10.5') >= 0 ? '10.5' : lofts[0]];
    if (c === 'hybrid') return [lofts.indexOf('22') >= 0 ? '22' : lofts[0]];
    return [lofts[0]];
  }
  function loftOptions(h) {
    var lo = arr(h.loft_options);
    if (lo.length) return lo;
    return arr(h.lofts).map(function (l) { return { loft: num(l), bounce: null, grind: null, label: l + '°' }; });
  }
  function availableLofts(h, hand) {
    var l = arr(h.lofts), lh = hand === 'Left' ? lhLofts(h) : null;
    return lh ? l.filter(function (x) { return lh.indexOf(x) >= 0; }) : l;
  }
  function defaultLoftOption(h) { var lo = loftOptions(h); return lo.length ? lo[0].label : null; }
  function materials(cat, h) {
    return uniq(cat.shaftOptions(h).map(function (o) { return o.material; })).sort(function (a, b) { return a === 'Steel' ? -1 : b === 'Steel' ? 1 : 0; });
  }
  function bucketsFor(cat, h, material) {
    var have = uniq(cat.shaftOptions(h).filter(function (o) { return o.material === material && o.bucket; }).map(function (o) { return o.bucket; }));
    return FLEX_BUCKETS.map(function (b) { return { value: b, available: have.indexOf(b) >= 0 }; });
  }
  function defaultBucket(cat, h, material) {
    var b = bucketsFor(cat, h, material).filter(function (x) { return x.available; }).map(function (x) { return x.value; });
    if (!b.length) return null;
    return b.indexOf('Regular') >= 0 ? 'Regular' : b.indexOf('Stiff') >= 0 ? 'Stiff' : b[0];
  }
  function shaftsFor(cat, h, material, bucket) {
    return cat.shaftOptions(h).filter(function (o) { return o.material === material && (o.bucket === bucket || !o.bucket); });
  }
  function defaultShaft(cat, h, material, bucket) {
    var l = shaftsFor(cat, h, material, bucket);
    var stock = l.filter(function (o) { return o.up === 0; });
    return (stock[0] || l[0] || {}).key || null;
  }
  function defaultGripSize(cat, gripHandle) {
    var g = cat.grips.filter(function (x) { return x.handle === gripHandle; })[0];
    var s = g ? arr(g.sizes) : [];
    if (!s.length) return null;
    return s.indexOf('Standard') >= 0 ? 'Standard' : s[0];
  }

  /** Re-validate a state after any change; snaps invalid choices to defaults. */
  function normalise(cat, heads, st) {
    var h = cat.head(st.head) || heads[0];
    st.head = h.handle;
    var fins = arr(h.finish_options).length ? arr(h.finish_options) : (h.finish ? [h.finish] : []);
    if (fins.length && fins.indexOf(st.finish) < 0) st.finish = fins[0];
    var hos = hosels(h, st.finish);
    if (hos.length && hos.indexOf(st.hosel) < 0) st.hosel = firstHosel(h, st.finish);
    if (!hos.length) st.hosel = null;
    if (!lhAvailable(h) && st.hand === 'Left') st.hand = 'Right';
    var c = category(h), lofts = arr(h.lofts);
    if (st.hand === 'Left' && lhLofts(h)) lofts = lofts.filter(function (l) { return lhLofts(h).indexOf(l) >= 0; });
    if (c === 'iron') { st.clubs = arr(st.clubs).filter(function (x) { return lofts.indexOf(x) >= 0; }); if (!st.clubs.length) st.clubs = defaultClubs(h); st.clubs.sort(function (a, b) { return lofts.indexOf(a) - lofts.indexOf(b); }); }
    else if (c === 'wedge') { var lo = loftOptions(h).map(function (x) { return x.label; }); if (lo.indexOf(st.loft) < 0) st.loft = lo[0]; st.clubs = [st.loft]; }
    else if (c === 'putter' || c === 'driving-iron') st.clubs = [c === 'putter' ? 'Putter' : 'DI'];
    else { if (lofts.indexOf(arr(st.clubs)[0]) < 0) { var dc = defaultClubs(h)[0]; st.clubs = [lofts.indexOf(dc) >= 0 ? dc : lofts[0]]; } st.clubs = [st.clubs[0]]; }
    var mats = materials(cat, h);
    if (mats.indexOf(st.material) < 0) st.material = mats.indexOf('Steel') >= 0 ? 'Steel' : mats[0] || null;
    var bs = bucketsFor(cat, h, st.material).filter(function (b) { return b.available; }).map(function (b) { return b.value; });
    if (bs.length && bs.indexOf(st.bucket) < 0) st.bucket = defaultBucket(cat, h, st.material);
    if (!bs.length) st.bucket = null;
    var sh = shaftsFor(cat, h, st.material, st.bucket).map(function (o) { return o.key; });
    if (sh.indexOf(st.shaft) < 0) st.shaft = defaultShaft(cat, h, st.material, st.bucket);
    var gr = cat.gripOptions(h).map(function (g) { return g.handle; });
    if (gr.indexOf(st.grip) < 0) st.grip = (h.default_grip && gr.indexOf(h.default_grip) >= 0) ? h.default_grip : (gr.indexOf(DEFAULT_GRIP) >= 0 ? DEFAULT_GRIP : gr[0]);
    var g = cat.grips.filter(function (x) { return x.handle === st.grip; })[0];
    var sizes = g ? arr(g.sizes) : [];
    if (sizes.length && sizes.indexOf(st.gripSize) < 0) st.gripSize = defaultGripSize(cat, st.grip);
    if (!sizes.length) st.gripSize = null;
    var s = cat.setting(h);
    ['loftAdj:loft_adjust', 'lieAdj:lie_adjust', 'length:length_adjust', 'papers:grip_papers', 'install:grip_install'].forEach(function (p) {
      var k = p.split(':'), list = arr(s[k[1]]);
      if (!list.length) { st[k[0]] = k[0] === 'length' ? (s.default_length || 'Standard') : (k[0] === 'papers' || k[0] === 'install' ? null : 'Standard'); return; }
      if (list.indexOf(st[k[0]]) < 0) st[k[0]] = k[0] === 'length' && s.default_length && list.indexOf(s.default_length) >= 0 ? s.default_length : (list.indexOf('Standard') >= 0 ? 'Standard' : list[0]);
    });
    return st;
  }

  /** Price breakdown in cents. */
  function price(cat, st) {
    var h = cat.head(st.head);
    var opt = cat.shaftOptions(h).filter(function (o) { return o.key === st.shaft; })[0];
    var grip = cat.grips.filter(function (g) { return g.handle === st.grip; })[0];
    var graphite = st.material === 'Graphite' && num(h.graphite_price_aud) > 0;
    var headC = cents(graphite ? h.graphite_price_aud : h.base_price_aud);
    var shaftC = opt ? opt.up : 0;
    var gripC = grip ? cents(grip.upcharge_aud) : 0;
    var n = arr(st.clubs).length || 1;
    var per = headC + shaftC + gripC;
    return { head: headC, headBasis: graphite ? 'Graphite' : (h.graphite_price_aud ? 'Steel' : 'Standard'), shaft: shaftC, grip: gripC, perClub: per, clubs: n, total: per * n,
      shaftOpt: opt || null, gripRec: grip || null, headRec: h };
  }

  /** Human summary used for line-item properties and the build summary. */
  function summary(cat, st) {
    var p = price(cat, st), h = p.headRec, c = category(h), s = cat.setting(h), out = [];
    out.push(['Model', h.label]);
    if (st.finish && (arr(h.finish_options).length > 1 || !h.finish)) out.push(['Head colour', st.finish]);
    out.push(['Hand', st.hand === 'Left' ? 'Left-handed' : 'Right-handed']);
    if (c === 'wedge') out.push(['Loft - Bounce', st.loft]);
    else if (c === 'iron') out.push(['Clubs', st.clubs.join(', ')]);
    else if (c !== 'putter' && c !== 'driving-iron') out.push(['Loft', loftLabel(h, st.clubs[0])]);
    if (st.hosel) out.push(['Hosel', st.hosel]);
    if (p.shaftOpt) out.push(['Shaft', p.shaftOpt.label + (st.material && c !== 'putter' ? ' - ' + st.material : '')]);
    if (p.gripRec) out.push(['Grip', p.gripRec.label + (st.gripSize ? ' - ' + st.gripSize : '')]);
    if (arr(s.loft_adjust).length) out.push(['Loft adjustment', st.loftAdj]);
    if (arr(s.lie_adjust).length) out.push(['Lie', st.lieAdj]);
    if (arr(s.length_adjust).length) out.push(['Length', st.length]);
    if (st.papers) out.push(['Grip papers', st.papers]);
    if (st.install) out.push(['Grip install', st.install]);
    if (st.hand === 'Left' && /custom order/i.test(h.lh || '')) out.push(['Note', 'Left-hand custom order from USA']);
    return out;
  }

  /**
   * Cart lines for /cart/add.js. variants.heads[headHandle][basis] -> variant id,
   * variants.upgrades[cents] -> variant id. Throws if a variant is missing.
   */
  function cartLines(cat, st, buildId) {
    var p = price(cat, st), h = p.headRec, c = category(h), v = cat.variants;
    var hv = (v.heads[h.handle] || {})[p.headBasis] || (v.heads[h.handle] || {}).Standard;
    if (!hv) throw new Error('No build variant for ' + h.handle + ' / ' + p.headBasis);
    var props = {};
    summary(cat, st).forEach(function (kv) { props[kv[0]] = kv[1]; });
    var sig = [p.head, p.shaft, p.grip, p.clubs].join(':');
    var lines = [];
    var base = function (extra) { var o = { _build_id: buildId, _build_sig: sig }; for (var k in extra) o[k] = extra[k]; return o; };
    var label = h.label + (c === 'iron' ? ' (' + st.clubs.join(', ') + ')' : '');
    [['shaft', p.shaft, p.shaftOpt && p.shaftOpt.label], ['grip', p.grip, p.gripRec && p.gripRec.label]].forEach(function (u) {
      if (!u[1]) return;
      var uv = v.upgrades[String(u[1])];
      if (!uv) throw new Error('No upgrade variant for ' + money(u[1]));
      lines.push({ id: uv, quantity: p.clubs, properties: base({ 'Upgrade': (u[0] === 'shaft' ? 'Shaft: ' : 'Grip: ') + u[2], 'For': label, _role: 'upgrade' }) });
    });
    // One head line; quantity = number of clubs (irons) or 1. Club list travels in the properties.
    // Added LAST: Shopify lists newest lines first, so the head shows above its upgrades in the cart.
    lines.push({ id: hv, quantity: p.clubs, properties: base(Object.assign({}, props, { _role: 'head' })) });
    return lines;
  }

  /**
   * Validate a cart (array of /cart.js items). Returns [{id, lines:[keys], reason}] for every build that no longer
   * matches what the configurator added, plus any build/upgrade line that was added outside the configurator.
   */
  function isBuildItem(it) { return /^pxg-custom-(build-|upgrade)/.test(it.handle || '') || /^C[BU]-/.test(it.sku || ''); }
  function auditCart(items) {
    var builds = {}, broken = [];
    items.forEach(function (it) {
      var p = it.properties || {}, id = p._build_id;
      if (!id) { if (isBuildItem(it)) broken.push({ id: null, lines: [it.key], reason: 'stray' }); return; }
      var b = builds[id] || (builds[id] = { heads: 0, headPriceOk: true, sig: p._build_sig, ups: {}, lines: [] });
      b.lines.push(it.key);
      var parts = String(p._build_sig || '').split(':').map(Number);
      if (p._role === 'head') { b.heads += it.quantity; if (it.price !== parts[0]) b.headPriceOk = false; }
      else b.ups[it.price] = (b.ups[it.price] || 0) + it.quantity;
    });
    Object.keys(builds).forEach(function (id) {
      var b = builds[id], parts = String(b.sig || '').split(':').map(Number);
      var clubs = parts[3], want = {};
      if (parts[1]) want[parts[1]] = (want[parts[1]] || 0) + clubs;
      if (parts[2]) want[parts[2]] = (want[parts[2]] || 0) + clubs;
      var ok = clubs > 0 && b.headPriceOk && b.heads === clubs && Object.keys(want).length === Object.keys(b.ups).length &&
        Object.keys(want).every(function (k) { return b.ups[k] === want[k]; });
      if (!ok) broken.push({ id: id, lines: b.lines, reason: 'changed' });
    });
    return broken;
  }

  return {
    FLEX_BUCKETS: FLEX_BUCKETS, Catalogue: Catalogue, category: category, settingKey: settingKey,
    defaults: defaults, normalise: normalise, price: price, summary: summary, cartLines: cartLines, auditCart: auditCart, isBuildItem: isBuildItem,
    materials: materials, bucketsFor: bucketsFor, shaftsFor: shaftsFor, loftOptions: loftOptions, loftLabel: loftLabel,
    hosels: hosels, money: money, cents: cents, lhLofts: lhLofts, availableLofts: availableLofts, lhAvailable: lhAvailable, gripClubType: gripClubType
  };
});
